/**
 * Componente React Móvil para la consulta y descarga de pases: /passtpx
 * Permite a los aficionados buscar sus pases oficiales por Correo o Teléfono registrado.
 * Soporta visualización y descarga múltiple de pases para familias (un mismo correo/teléfono con varios tickets).
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { InitializeAnalytics } from '../../../utilities/Analytics';
import {
    GetPublicConfig,
    RecoverPass,
    TrackMetric,
    GetOrCreateSessionId,
    MaskPhone,
    MaskEmail,
    FormatMexicoDate,
    DownloadPassCard
} from '../../../utilities/BL/Xtp';

export const XtpPassFan = () => {
    const [searchParams] = useSearchParams();

    // 1. Estados principales de ciclo de vida
    const [config, setConfig] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [passes, setPasses] = useState([]);
    const [activePassIndex, setActivePassIndex] = useState(0);

    // Estados de descarga y cooldown anti-bruteforce
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);
    const [downloadSuccessToast, setDownloadSuccessToast] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState(0);

    const passCardRef = useRef(null);
    const searchStartTrackedRef = useRef(false);

    // Temporizador de Cooldown en segundos
    useEffect(() => {
        if (cooldownRemaining <= 0) return;
        const interval = setInterval(() => {
            setCooldownRemaining(prev => Math.max(0, prev - 1));
        }, 1000);
        return () => clearInterval(interval);
    }, [cooldownRemaining]);

    // 2. Inicializar GTM/GA4, cargar configuración pública de XTP y registrar visita al montar
    useEffect(() => {
        InitializeAnalytics();

        const loadInitialConfig = async () => {
            try {
                const res = await GetPublicConfig();
                if (res && res.success && res.data) {
                    setConfig(res.data);
                }
            } catch (err) {
                console.error('Error al cargar configuración en XtpPassFan:', err);
            }
        };

        loadInitialConfig();
        const sId = GetOrCreateSessionId();
        TrackMetric('page_view_passtpx', 'direct_pass_lookup', sId);

        // Detectar si el usuario ingresó escaneando un código QR
        const hasQrParam = searchParams.get('s') === 'qr' || searchParams.get('src') === 'qr';
        if (hasQrParam) {
            TrackMetric('qr_scan_passtpx', 'qr_passtpx_direct', sId);
        }
    }, [searchParams]);

    // Registrar intención de inicio de búsqueda al enfocar el input
    const handleInputFocus = () => {
        if (!searchStartTrackedRef.current) {
            searchStartTrackedRef.current = true;
            TrackMetric('search_start_passtpx', 'direct_pass_lookup', GetOrCreateSessionId());
        }
    };

    // 3. Manejador de búsqueda de pases por correo o teléfono
    const handleSearchPasses = async (e) => {
        if (e) e.preventDefault();
        if (cooldownRemaining > 0) return;
        if (config && config.isPassLookupActive === false) {
            setErrorMessage('La consulta de pases se encuentra temporalmente en pausa.');
            return;
        }

        const cleanQuery = searchQuery.trim();

        if (!cleanQuery) {
            setErrorMessage('Por favor escribe tu correo electrónico o teléfono celular registrado.');
            return;
        }

        setIsSearching(true);
        setErrorMessage('');
        TrackMetric('search_query_passtpx', 'direct_pass_lookup', GetOrCreateSessionId());

        try {
            const res = await RecoverPass(cleanQuery, GetOrCreateSessionId());

            if (res && res.success && Array.isArray(res.passes) && res.passes.length > 0) {
                setPasses(res.passes);
                setActivePassIndex(0);
                setCooldownRemaining(0);
                TrackMetric('pass_lookup_success', 'direct_pass_lookup', GetOrCreateSessionId());
            } else {
                setPasses([]);
                if (res?.isRateLimited) {
                    const waitSec = res.blockRemainingSeconds || 300;
                    setCooldownRemaining(waitSec);
                    setErrorMessage('Demasiados intentos fallidos. Por tu seguridad, espera unos minutos antes de intentar de nuevo.');
                } else {
                    setErrorMessage(res?.message || 'No se encontraron pases activos asociados a los datos ingresados. Verifica tu información.');
                }
                TrackMetric('pass_lookup_not_found', 'direct_pass_lookup', GetOrCreateSessionId());
            }
        } catch (err) {
            console.error('Error en búsqueda de pases:', err);
            setErrorMessage('Ocurrió un error al consultar tus pases. Por favor intenta de nuevo.');
        } finally {
            setIsSearching(false);
        }
    };

    // 4. Descargar el pase activo en pantalla
    const handleDownloadCurrentPass = async () => {
        if (passes.length === 0 || isDownloading) return;
        const currentPass = passes[activePassIndex];
        if (!currentPass) return;

        setIsDownloading(true);
        TrackMetric('pass_saved_passtpx', 'direct_pass_lookup', GetOrCreateSessionId());

        try {
            const success = await DownloadPassCard(currentPass, {
                qrElementSelector: '#xtp-pass-card svg',
                bannerTitle: 'PASE PERSONAL EXCLUSIVO',
                hideHeader: true,
                hidePrize: true,
                hideLocationNote: true,
                sponsorLogoUrl: '/img/page/xolos-five-black.png',
                backgroundImageUrl: '/img/page/backgrounds/Fondo-SelfieFan-Varonil.jpeg'
            });

            if (success) {
                setDownloadSuccessToast(true);
                setTimeout(() => setDownloadSuccessToast(false), 3500);
            }
        } catch (err) {
            console.error('Error al descargar tarjeta:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    // 5. Descargar todos los pases para familias en un solo clic
    const handleDownloadAllPasses = async () => {
        if (passes.length === 0 || isDownloadingAll) return;
        setIsDownloadingAll(true);
        TrackMetric('pass_saved_passtpx', 'direct_pass_lookup', GetOrCreateSessionId());

        try {
            for (let i = 0; i < passes.length; i++) {
                const p = passes[i];
                await DownloadPassCard(p, {
                    bannerTitle: 'PASE PERSONAL EXCLUSIVO',
                    hideHeader: true,
                    hidePrize: true,
                    hideLocationNote: true,
                    sponsorLogoUrl: '/img/page/xolos-five-black.png',
                    backgroundImageUrl: '/img/page/backgrounds/Fondo-SelfieFan-Varonil.jpeg'
                });
                // Pausa breve entre descargas de navegador
                await new Promise((resolve) => setTimeout(resolve, 600));
            }
            setDownloadSuccessToast(true);
            setTimeout(() => setDownloadSuccessToast(false), 4000);
        } catch (err) {
            console.error('Error al descargar todos los pases:', err);
        } finally {
            setIsDownloadingAll(false);
        }
    };

    const currentPass = passes[activePassIndex] || null;

    return (
        <div
            className="XtpFan"
            style={{
                backgroundImage: "url('/img/page/backgrounds/Fondo-SelfieFan-Varonil.jpeg')",
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed'
            }}
        >
            {/* Fondo Multimedia con Overlay para Contraste */}
            <div className="XtpFan__Overlay" style={{ background: 'rgba(0, 0, 0, 0.45)' }}></div>

            <div className="XtpFan__Container">
                {/* Membrete y Logotipos Oficiales */}
                <header className="XtpFan__Header text-center pt-2 pb-1">
                    <img
                        src="/img/page/xolos-five.png"
                        alt="Xolos Five"
                        className="XtpFan__Header__Logo img-fluid d-block mx-auto mb-2"
                        style={{ maxHeight: '70px', objectFit: 'contain' }}
                        onError={(e) => { e.target.src = '/img/favicon/Big.png'; }}
                    />
                    <h1 className="font-cinzel text-white text-3xl font-black mb-1">
                        Tradiciones Perronas
                    </h1>
                    <p className="text-white small mb-0">
                        Consulta y descarga de pases oficiales de acceso
                    </p>
                </header>

                {/* Toast de confirmación de descarga */}
                {downloadSuccessToast && (
                    <div
                        className="alert alert-success position-fixed top-0 start-50 translate-middle-x mt-3 z-3 shadow-lg border-0 rounded-4 text-center py-2 px-4"
                        style={{ maxWidth: '90%' }}
                        role="alert"
                    >
                        <i className="bi bi-check-circle-fill me-2 fs-5 align-middle"></i>
                        <span className="fw-bold">
                            {passes.length > 1 && isDownloadingAll
                                ? '¡Todos los pases han sido descargados en tu galería!'
                                : '¡Pase oficial guardado con éxito en tu galería!'}
                        </span>
                    </div>
                )}

                {/* =======================================================
                 * VISTA 1: FORMULARIO DE CONSULTA O AVISO DE PAUSA
                 * ======================================================= */}
                {config && config.isPassLookupActive === false ? (
                    <div className="XtpFan__Card text-center py-4 px-3">
                        <div className="mb-3">
                            <i className="bi bi-pause-circle-fill text-warning" style={{ fontSize: '3.5rem' }}></i>
                        </div>
                        <h2 className="font-cinzel text-white fs-4 fw-bold mb-2">
                            Consultas en Pausa
                        </h2>
                        <div className="p-3 bg-dark bg-opacity-50 rounded-3 border border-secondary border-opacity-25 mb-3 text-white small">
                            <p className="mb-2">
                                La búsqueda y descarga de pases digitales se encuentra temporalmente en pausa por actualización o logística oficial del Club.
                            </p>
                            <p className="mb-0 text-white-50">
                                Por favor vuelve a consultar más tarde o acércate con el personal autorizado en tienda oficial o accesos del estadio.
                            </p>
                        </div>
                        <div className="d-flex align-items-center justify-content-center gap-2 text-warning small fw-semibold">
                            <i className="bi bi-clock-history"></i>
                            <span>Gracias por tu comprensión y afición perrona</span>
                        </div>
                    </div>
                ) : passes.length === 0 ? (
                    <div className="XtpFan__Card">
                        <div className="text-center mb-3">
                            <i className="bi bi-qr-code-scan text-warning" style={{ fontSize: '3rem' }}></i>
                            <h2 className="font-cinzel text-white fs-4 fw-bold mt-2 mb-1">
                                Consulta tu Pase Oficial
                            </h2>
                            <p className="small mb-0 text-white" style={{ color: '#f1f5f9', opacity: 0.9 }}>
                                Escribe el correo electrónico o teléfono con el que te registraste para generar tu pase de acceso.
                            </p>
                        </div>

                        <form onSubmit={handleSearchPasses} noValidate>
                            <div className="XtpFan__FormGroup mb-3">
                                <label htmlFor="xtp-pass-query" className="fw-bold mb-2 text-white">
                                    Correo o Teléfono registrado: <span className="req">*</span>
                                </label>
                                <input
                                    id="xtp-pass-query"
                                    type="text"
                                    className="XtpFan__Input"
                                    placeholder="Ej. aficionado@correo.com o 6641234567"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onFocus={handleInputFocus}
                                    disabled={isSearching || cooldownRemaining > 0}
                                    autoComplete="email"
                                    autoFocus
                                />
                            </div>

                            {cooldownRemaining > 0 && (
                                <div className="alert alert-warning py-2 px-3 small border-0 rounded-3 mb-3 text-center fw-bold text-dark shadow-sm">
                                    <i className="bi bi-clock-history me-1"></i>
                                    Espera {Math.floor(cooldownRemaining / 60)}:{(cooldownRemaining % 60).toString().padStart(2, '0')} min antes de intentar de nuevo.
                                </div>
                            )}

                            {errorMessage && (
                                <div className="alert alert-danger py-2 px-3 small border-0 rounded-3 mb-3 text-center fw-semibold">
                                    <i className="bi bi-exclamation-triangle-fill me-1 align-middle"></i>
                                    {errorMessage}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="btn btn-danger w-100 py-3 rounded-3 fw-bold text-uppercase fs-6 shadow-lg d-flex align-items-center justify-content-center gap-2"
                                style={{
                                    backgroundColor: '#dc2626',
                                    borderColor: '#dc2626',
                                    letterSpacing: '0.04em'
                                }}
                                disabled={isSearching || !searchQuery.trim() || cooldownRemaining > 0}
                            >
                                {isSearching ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        <span>Buscando tus pases...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-search fs-5"></i>
                                        <span>Consultar mis Pases</span>
                                    </>
                                )}
                            </button>

                            <div className="mt-3 text-center small px-2 text-white" style={{ fontSize: '0.85rem', color: '#f1f5f9' }}>
                                <i className="bi bi-info-circle me-1 text-warning"></i>
                                Si registraste a varios integrantes de tu familia con el mismo correo o teléfono, el sistema te mostrará todos sus pases aquí.
                            </div>
                        </form>
                    </div>
                ) : (
                    /* =======================================================
                     * VISTA 2: VISOR DE PASES CON SOPORTE MULTIPASE FAMILIAR
                     * ======================================================= */
                    <div className="XtpFan__PassView">
                        {/* Banner y Navegador si hay múltiples pases (Familias / Niños) */}
                        {passes.length > 1 && (
                            <div className="mb-3">
                                <div className="alert border-0 bg-warning bg-opacity-25 text-white py-2 px-3 rounded-3 text-center mb-2 shadow-sm">
                                    <span className="fw-black text-warning">🎟️ ¡Tienes {passes.length} pases disponibles!</span>
                                    <div className="small text-white-50" style={{ fontSize: '0.78rem' }}>
                                        Navega entre ellos para ver y guardar el pase de cada familiar.
                                    </div>
                                </div>

                                <div className="XtpFan__PassNavigation">
                                    <button
                                        type="button"
                                        onClick={() => setActivePassIndex((prev) => Math.max(0, prev - 1))}
                                        disabled={activePassIndex <= 0}
                                        aria-label="Pase anterior"
                                    >
                                        <i className="bi bi-chevron-left"></i>
                                    </button>
                                    <div className="d-flex flex-column align-items-center text-center">
                                        <span className="counter fw-bold text-warning">
                                            Pase {activePassIndex + 1} de {passes.length}
                                        </span>
                                        <span className="small text-white text-truncate max-w-xs fw-semibold">
                                            {currentPass?.name || 'Aficionado'}
                                        </span>
                                        <div className="d-flex gap-1 mt-1">
                                            {passes.map((p, idx) => (
                                                <span
                                                    key={p.passCode || idx}
                                                    className={`XtpFan__PaginatorDot ${idx === activePassIndex ? 'active' : ''}`}
                                                    onClick={() => setActivePassIndex(idx)}
                                                    title={`Ver pase de ${p.name || idx + 1}`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setActivePassIndex((prev) => Math.min(passes.length - 1, prev + 1))}
                                        disabled={activePassIndex >= passes.length - 1}
                                        aria-label="Siguiente pase"
                                    >
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Botón Principal de Descarga Superior */}
                        <div className="text-center mb-3 mx-auto" style={{ maxWidth: '440px' }}>
                            <p className="small mb-2 text-white" style={{ fontSize: '0.85rem' }}>
                                Aquí está tu pase oficial de acceso. Guárdalo o toma captura de pantalla
                            </p>
                            <div className="d-flex flex-column gap-2">
                                <button
                                    type="button"
                                    className="btn btn-warning w-100 py-3 rounded-3 fw-black text-uppercase shadow-lg fs-6 d-flex align-items-center justify-content-center gap-2"
                                    style={{
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        color: '#ffffff',
                                        border: 'none',
                                        letterSpacing: '0.04em'
                                    }}
                                    onClick={handleDownloadCurrentPass}
                                    disabled={isDownloading || isDownloadingAll}
                                >
                                    {isDownloading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            <span>Guardando pase...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-camera-fill fs-5"></i>
                                            <span>Guardar este Pase en mi Galería</span>
                                        </>
                                    )}
                                </button>

                                {passes.length > 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-warning w-100 py-2 rounded-3 fw-bold small text-uppercase d-flex align-items-center justify-content-center gap-2"
                                        onClick={handleDownloadAllPasses}
                                        disabled={isDownloading || isDownloadingAll}
                                    >
                                        {isDownloadingAll ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                <span>Descargando los {passes.length} pases...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-download"></i>
                                                <span>Guardar todos mis {passes.length} pases</span>
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* =======================================================
                         * TARJETA DORADA DE EXHIBICIÓN (ESTRUCTURA OFICIAL XTP)
                         * ======================================================= */}
                        {currentPass && (
                            <div className="XtpFan__PassCard" id="xtp-pass-card" ref={passCardRef}>
                                <div className="XtpFan__ShimmerBanner">
                                    <div className="XtpFan__ShimmerBannerOverlay"></div>
                                    <h2 className="font-cinzel fs-4 font-black text-white text-uppercase tracking-widest mb-0 drop-shadow">
                                        Pase Personal Exclusivo
                                    </h2>
                                    <p className="text-white small fw-bold mt-1 mb-2 tracking-wide">
                                        Tradiciones Perronas
                                    </p>
                                    <div className="XtpFan__FolioBadge">
                                        {currentPass.passCode || '#TPX26-00000'}
                                    </div>
                                </div>

                                {/* QR con Logotipo Personalizado y Nivel Q */}
                                <div className="d-flex justify-content-center my-3 p-2 bg-light rounded-3 border">
                                    <QRCodeSVG
                                        value={currentPass.verificationToken || currentPass.passCode || 'XTP-2026'}
                                        size={175}
                                        bgColor="#ffffff"
                                        fgColor="#172554"
                                        level="Q"
                                        includeMargin={true}
                                        imageSettings={{
                                            src: '/img/favicon/Big.png',
                                            x: undefined,
                                            y: undefined,
                                            height: 42,
                                            width: 42,
                                            excavate: true
                                        }}
                                    />
                                </div>

                                {/* Datos Protegidos del Aficionado */}
                                <div className="small text-muted mb-2 text-center">
                                    <span className="fw-bold text-dark fs-6">
                                        {(currentPass.name || 'AFICIONADO').toUpperCase()}
                                    </span>
                                    <br />
                                    <span>
                                        {MaskEmail(currentPass.email)} • {MaskPhone(currentPass.phone)}
                                    </span>
                                    <br />
                                    <span className="font-monospace text-dark fw-bold">
                                        Ticket: {currentPass.ticketNumber}
                                    </span>
                                    <br />
                                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                        Registrado el: {FormatMexicoDate(currentPass.createdAtUtc)}
                                    </span>
                                </div>

                                {/* Patrocinador y Logotipo Oficial (Estilo /tpx2026) */}
                                <div className="mt-3">
                                    <p className="text-muted text-uppercase mb-2 fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.15em' }}>
                                        Powered by
                                    </p>
                                    <div className="XtpFan__SponsorLogos">
                                        <img
                                            src="/img/page/xolos-five-black.png"
                                            alt="Club Tijuana"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Botón Inferior: Guardar Pase en la Galería */}
                        <div className="text-center mt-3 mx-auto" style={{ maxWidth: '440px' }}>
                            <button
                                type="button"
                                className="btn btn-warning w-100 py-3 rounded-3 fw-black text-uppercase shadow-lg fs-6 d-flex align-items-center justify-content-center gap-2"
                                style={{
                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                    color: '#ffffff',
                                    border: 'none',
                                    letterSpacing: '0.04em'
                                }}
                                onClick={handleDownloadCurrentPass}
                                disabled={isDownloading || isDownloadingAll}
                            >
                                {isDownloading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        <span>Guardando pase...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-camera-fill fs-5"></i>
                                        <span>Guardar este Pase en mi Galería</span>
                                    </>
                                )}
                            </button>

                            {passes.length > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-outline-warning w-100 py-2 rounded-3 fw-bold small text-uppercase d-flex align-items-center justify-content-center gap-2 mt-2"
                                    onClick={handleDownloadAllPasses}
                                    disabled={isDownloading || isDownloadingAll}
                                >
                                    {isDownloadingAll ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            <span>Descargando los {passes.length} pases...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-download"></i>
                                            <span>Guardar todos mis {passes.length} pases</span>
                                        </>
                                    )}
                                </button>
                            )}

                            <p className="small mt-2 mb-0 text-white" style={{ fontSize: '0.85rem' }}>
                                Aquí está tu pase oficial de acceso. Guárdalo o toma captura de pantalla
                            </p>
                        </div>

                        {/* Botón para Realizar Otra Consulta */}
                        <div className="text-center mt-3 pt-2">
                            <button
                                type="button"
                                className="btn btn-outline-light py-2 px-4 rounded-3 fw-bold small"
                                onClick={() => {
                                    setPasses([]);
                                    setSearchQuery('');
                                    setErrorMessage('');
                                }}
                            >
                                <i className="bi bi-arrow-left me-1"></i>
                                Consultar otro correo o teléfono
                            </button>
                        </div>
                    </div>
                )}

                {/* Footer Oficial Interaktia idéntico a /tpx2026 */}
                <footer className="XtpFan__Footer text-center py-3 mt-auto">
                    <p className="text-center FdmFooter FooterCredits mb-0">
                        <a
                            href="https://interaktia.com/?utm_source=xolo_folio_web&utm_medium=footer&utm_campaign=fdm_xolos"
                            target="_blank"
                            rel="noreferrer"
                            className="text-white small"
                            style={{ textDecoration: 'underline' }}
                        >
                            Hecho con ♥ por Interaktia
                        </a>
                    </p>
                </footer>
            </div>
        </div>
    );
};
